import { useCallback, useEffect, useRef, useState } from 'react';
import { apiListMyReferralDocuments, apiRemoveReferralDocument } from '../utils/rmsApi';
import { COULD_NOT_REMOVE_TITLE, removeFailureBody, removedNotice } from '../utils/referralDocumentPortal';

/*
 * The signed-in service user's side of a referral's documents (TDI-578,
 * TDI-579): the list, the type list, the cap and whether adding is still
 * open, all from rmsListMyReferralDocuments. The documents subcollection has
 * no client rule, so the callable is the only way to read it.
 *
 * `enabled` is false where the record has no referral (the SEN-only
 * pathway), so nothing is asked of the server there.
 */
export default function useMyReferralDocuments({ orgId, patientRecordId, enabled = true }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!enabled || !orgId || !patientRecordId) {
      setLoading(false);
      return null;
    }
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await apiListMyReferralDocuments({ orgId, patientRecordId });
      if (live.current) setSummary(data || null);
      return data;
    } catch (e) {
      if (live.current) setError(e || new Error('load failed'));
      return null;
    } finally {
      if (live.current) setLoading(false);
    }
  }, [enabled, orgId, patientRecordId]);

  useEffect(() => { load(); }, [load]);

  // Removal is confirmed by the server before the row leaves the list; the
  // list is then read again so the cap and count stay the server's.
  const remove = useCallback(async (documentId) => {
    await apiRemoveReferralDocument({ orgId, patientRecordId, documentId });
    if (live.current) {
      setSummary((s) => (s ? { ...s, documents: (s.documents || []).filter((d) => d.id !== documentId) } : s));
    }
    await load({ silent: true });
  }, [orgId, patientRecordId, load]);

  return { summary, loading, error, reload: load, remove };
}

/*
 * Remove with its confirm step (D-04 frame 05, D-05 P4, D-06 Q9): which
 * document the confirm is asking about, the busy flag, and the outcome as a
 * notice for the screen to show at the top ("... has been removed.", or
 * why it could not be).
 */
export function useDocumentRemoval({ remove, reload, onNotice }) {
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const confirm = useCallback(async () => {
    const doc = removing;
    if (!doc) return;
    setBusy(true);
    try {
      await remove(doc.id);
      setRemoving(null);
      onNotice({ kind: 'info', title: removedNotice(doc.description) });
    } catch (e) {
      setRemoving(null);
      onNotice({ kind: 'error', title: COULD_NOT_REMOVE_TITLE, body: removeFailureBody(e) });
      if (reload) reload({ silent: true });
    } finally {
      setBusy(false);
    }
  }, [removing, remove, reload, onNotice]);
  return { removing, setRemoving, busy, confirm, cancel: () => setRemoving(null) };
}
